import { Injectable } from '@angular/core';
import {
  CompareType,
  Comparison,
  CurrentItem,
  DeliveryDates,
  OfferDeliveryDates,
  ReplacementOffer,
  Specification,
} from '@dgx-pr-spa/shared/util';
import { BuildConfigService } from '@dgx-pr-spa/ui-components';
import { Store } from '@ngrx/store';
import { ProductDetailsService } from '@product-details/services/product-details.service';
import * as fromProductDetails from '@product-details/store/actions';
import { Observable } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import * as fromOffer from '../store/actions';
import * as fromOptimizely from '../store/actions';
import * as fromOfferReducers from '../store/reducers';
import * as fromOfferQuery from '../store/selectors';

@Injectable({
  providedIn: 'root',
})
export class ViewOffersService {
  constructor(
    public store: Store<fromOfferReducers.ViewOffersState>,
    private productDetailsService: ProductDetailsService,
    public readonly buildConfigService: BuildConfigService
  ) {}

  readonly offerReference$ = this.store.select(
    fromOfferQuery.getOfferReference
  );
  readonly offersState$ = this.store.select(fromOfferQuery.getOffersState);
  readonly offers$ = this.store.select(fromOfferQuery.getOffers);
  readonly offersLoading$ = this.store.select(fromOfferQuery.getOffersLoading);
  readonly offersLoaded$ = this.store.select(fromOfferQuery.getOffersLoaded);
  readonly deliveryAddress$ = this.store.select(
    fromOfferQuery.getDeliveryAddress
  );
  readonly currentItem$ = this.store.select(fromOfferQuery.getCurrentItem);
  readonly comparisons$ = this.store.select(fromOfferQuery.getComparisons);
  readonly selectedFilteredOffers$ = this.productDetailsService.selected$.pipe(
    withLatestFrom(this.offers$),
    map(([selected, offers]) =>
      this.filterOffersBySelectedType(selected, offers.replacement_offers)
    )
  );

  readonly currentPlan$ = this.store.select(fromOfferQuery.getCurrentPlan);

  readonly contact$ = this.store.select(fromOfferQuery.getContact);

  readonly selectOffersDeliveryDatesKeys$ = this.store.select(
    fromOfferQuery.selectOffersDeliveryDatesKeys
  );

  readonly selectOffersDeliveryDatesEntities$ = this.store.select(
    fromOfferQuery.selectOffersDeliveryDatesEntities
  );

  readonly selectOffersDeliveryDates$ = this.store.select(
    fromOfferQuery.selectOffersDeliveryDates
  );

  readonly optimizely$ = this.store.select(fromOfferQuery.getOptimizelyState);

  readonly selectOffersDeliveryDatesById$ = (
    id: string
  ): Observable<OfferDeliveryDates> =>
    this.store.select(fromOfferQuery.selectOffersDeliveryDatesById(id));

  readonly isNextDayDeliveryAvailable$ = (supplierID, catNum) => {
    const id = `${supplierID}/${catNum}`;
    return this.selectOffersDeliveryDatesById$(id).pipe(
      filter((entity) => !!entity),
      map(({ data }) => this.isNextDayDeliveryAvailable(data))
    );
  };

  filterOffersBySelectedType(selected, offers = [] as ReplacementOffer[]) {
    return this.filterOffers(selected, offers).sort((a, b) =>
      selected.upgrade_fee
        ? b.upgrade_fee - a.upgrade_fee
        : a.upgrade_fee - b.upgrade_fee
    );
  }

  filterOffers(selected, offers = [] as ReplacementOffer[]) {
    return offers.filter(({ uniqueId }) => selected.uniqueId !== uniqueId);
  }

  filterOfferSelected(selected, offers = [] as ReplacementOffer[]) {
    return !!offers.filter(({ uniqueId }) => selected.uniqueId === uniqueId)
      .length;
  }

  setOfferReference(payload: string) {
    this.store.dispatch(fromOffer.setOfferReference({ payload }));
  }

  clearOfferReference() {
    this.store.dispatch(fromOffer.clearOfferReference());
  }

  loadOffers(payload?: string) {
    this.store.dispatch(fromOffer.loadOffers({ payload }));
  }

  resetOffers() {
    this.store.dispatch(fromOffer.resetOffers());
  }

  setComparisons(payload: ReplacementOffer) {
    this.store.dispatch(fromOffer.setComparisons({ payload }));
  }

  setAllComparisons(payload: ReplacementOffer[]) {
    this.store.dispatch(fromOffer.setAllComparisons({ payload }));
  }

  setDirectComparisons(payload: ReplacementOffer) {
    this.store.dispatch(fromOffer.setDirectComparisons({ payload }));
  }

  clearComparisons() {
    this.store.dispatch(fromOffer.clearComparisons());
  }

  selectOffer(payload: ReplacementOffer) {
    this.store.dispatch(fromProductDetails.selectOffer({ payload }));
  }

  loadOffersDeliveryDates(payload: ReplacementOffer) {
    this.store.dispatch(fromOffer.loadOffersDeliveryDates({ payload }));
  }

  loadOptimizely(active: boolean) {
    this.store.dispatch(fromOptimizely.optimizelyLoaded({ payload: active }));
  }

  isNextDayDeliveryAvailable(dates: DeliveryDates) {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return (
      dates?.delivery_dates &&
      dates.delivery_dates.some(
        (date) => date.slice(0, 10) === tomorrow.toJSON().slice(0, 10)
      )
    );
  }

  isSelectedSpecsDifferentToCurrent(
    current: CurrentItem,
    selected: ReplacementOffer
  ): {
    hasDifference: boolean;
    current: Specification[];
    selected: Specification[];
  } {
    const comparisons = [current, selected] as Comparison[];
    const compareSpec = this.getUniqueHeadings(comparisons);
    const filterSpecs = [
      'Width',
      'Height',
      'Color',
      'Depth',
      'Construction',
      'Energy Class',
    ];

    let selectedProduct = compareSpec[1]
      .filter((item) => item.highlight)
      .filter(
        (item) =>
          filterSpecs.find((fs) => item.name.includes(fs)) &&
          item.value !== 'No data available' &&
          item.value !== 'N/A'
      );

    const productSelectedHeadings = selectedProduct.map((i) => i.name);
    const currentProduct = compareSpec[0].filter(
      (item) =>
        productSelectedHeadings.includes(item.name) &&
        item.value !== 'No data available' &&
        item.value !== 'N/A'
    );

    selectedProduct = selectedProduct.filter((item) => {
      return (
        currentProduct.findIndex((cpItem) => cpItem.name.includes(item.name)) >
        -1
      );
    });

    return {
      hasDifference: !!currentProduct.length,
      current: currentProduct,
      selected: selectedProduct,
    };
  }

  isAnySpecsDifferentToCurrent(comparisons: Comparison[]) {
    const compareSpec = this.getUniqueHeadings(comparisons);
    const highlightsArr = compareSpec.map(
      (product) => product.filter((item) => item.highlight).length
    );
    return !!highlightsArr.reduce((a, b) => a + b);
  }

  getUniqueHeadings(comparisons = [] as Comparison[]) {
    let uniqueHeadings: string[] = [];

    comparisons.map((comparison: Comparison) => {
      comparison?.compare_specs?.map((spec) => uniqueHeadings.push(spec.name));
    });

    uniqueHeadings = Array.from(new Set(uniqueHeadings)).sort(
      this.sortHeadings
    );

    if (this.buildConfigService.config?.isBVOverrideEnabled) {
      this.filterSpecs(uniqueHeadings);
    }

    return this.createCompareSpecArray(uniqueHeadings, comparisons);
  }

  filterSpecs(uniqueHeadings) {
    const blackList = ['Alias'];
    blackList.forEach((item) => {
      const index = uniqueHeadings.indexOf(item);
      if (index > -1) {
        uniqueHeadings.splice(index, 1);
      }
    });
  }

  /**
   * Array to store each row spec differences
   */
  createFilteredCompareSpecRow(specArray: Specification[][]) {
    const filteredHighlight = specArray.map((spec: Specification[]) =>
      spec.filter((s) => s.highlight === true)
    );

    const uniqueHeadings = Array.from(
      new Set(
        []
          .concat(...filteredHighlight)
          .map((n: Specification) => n.name)
          .sort(this.sortHeadings)
      )
    );

    return specArray.map((s) =>
      s.map((spec) => uniqueHeadings.includes(spec.name))
    )[0];
  }

  sortHeadings(a: string, b: string) {
    a = a.toLowerCase();
    b = b.toLowerCase();
    return a < b ? -1 : b < a ? 1 : 0;
  }

  /**
   * Remap comparison specifications to new array
   */
  createCompareSpecArray(
    uniqueHeadings: string[],
    models = [] as Comparison[]
  ) {
    const compareSpecs: Specification[][] = [];
    const comparisons: Comparison[] = JSON.parse(JSON.stringify(models || []));

    comparisons.map((comparison: Comparison, i: number) => {
      const mappedCompareSpecs = [] as Specification[];

      uniqueHeadings.map((name) => {
        const currentItemMissingSpec = comparisons[0]?.compare_specs.find(
          (s) => s.name === name
        );

        comparisons[0].compare_specs.map((item) => {
          item.compare = CompareType.EXCLUDED;
        });

        const specification = (comparison.compare_specs.filter((spec) => {
          return spec.name === name;
        })[0] as Specification) || {
          name,
          value: 'No data available',
          compare:
            i === 0
              ? CompareType.EXCLUDED
              : currentItemMissingSpec
              ? null
              : CompareType.EXCLUDED,
        };

        mappedCompareSpecs.push(specification);
      });

      if (this.buildConfigService.config?.isBVOverrideEnabled) {
        // using string comparison
        compareSpecs.push(mappedCompareSpecs);
      } else {
        // using BV matrix comparison
        compareSpecs.push(this.specHighlight(mappedCompareSpecs));
      }
    });

    if (this.buildConfigService.config?.isBVOverrideEnabled) {
      // using string comparison
      this.stringCompareProperties(compareSpecs);
    } else {
      // using BV matrix comparison
      this.matchEmptyCompareProperties(compareSpecs);
    }

    return compareSpecs;
  }

  specHighlight(specifications: Specification[]): Specification[] {
    return specifications.map((s) => {
      s.highlight =
        s.compare === CompareType.BETTER ||
        s.compare === CompareType.WORSE ||
        s.compare === null;
      return s;
    });
  }

  matchEmptyCompareProperties(specifications: Specification[][]) {
    specifications.map((spec, i) => {
      return i === 0
        ? spec
        : spec.map((item, j) => {
            if (
              !Object.prototype.hasOwnProperty.call(item, 'compare') &&
              item.value.toUpperCase() !==
                specifications[0][j].value.toUpperCase()
            ) {
              item.highlight = true;
            }
          });
    });
  }

  // String comparison solution as BV data unreliable
  stringCompareProperties(specifications: Specification[][]) {
    specifications.map((spec, i) => {
      return i === 0
        ? spec
        : spec.map((item, j) => {
            if (
              item.value.toUpperCase() !==
              specifications[0][j].value.toUpperCase()
            ) {
              item.highlight = true;
            }
          });
    });
  }

  /**
   * Scrolls into view
   */
  scrollIntoView(id: string, behavior?: ScrollBehavior) {
    document.getElementById(id)?.scrollIntoView({
      behavior,
      block: 'start',
      inline: 'nearest',
    });
  }
}
